//changed due to package upgrade
import React, { useState } from 'react';
import AppGridContainer from '@projectK/components/AppGridContainer';
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import EditIcon from '@mui/icons-material/Edit';
import AppCard from '@projectK/components/AppCard';
import { useInfoViewActionsContext } from '@projectK/context';
import jwtAxios from '@projectK/services/auth/JWT';
import AppInfoView from '@projectK/components/AppInfoView';
import * as yup from 'yup';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppTextField from '@projectK/components/AppTextField';
import PropTypes from 'prop-types';

const validationSchema = yup.object({
  facebook: yup
    .string()
    .matches(
      /^(https?:\/\/)?(www\.)?facebook\.com\/.+/,
      'Invalid Facebook link.',
    )
    .nullable()
    .optional(),
  twitter: yup
    .string()
    .matches(/^(https?:\/\/)?(www\.)?x\.com\/\w{1,30}\/?$/, 'Invalid X link.')
    .nullable()
    .optional(),
  instagram: yup
    .string()
    .matches(
      /^(https?:\/\/)?(www\.)?instagram\.com\/.+$/,

      'Invalid Instagram link.',
    )
    .nullable()
    .optional(),
  slack: yup
    .string()
    .matches(
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)?slack\.com(\/[a-zA-Z0-9_-]*)*\/?(\?[^\s#]*)?(#[^\s]*)?$/,
      'Invalid Slack link.',
    )
    .nullable()
    .optional(),
});

const Social = ({ settings, fetchSettings }) => {
  const companyTheme = CompanyTheme();
  const [isEdit, setIsEdit] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();

  return (
    <>
      <AppCard>
        <Formik
          enableReinitialize={true}
          validateOnChange={true}
          initialValues={{
            facebook: settings?.facebook || '',
            instagram: settings?.instagram || '',
            slack: settings?.slack || '',
            twitter: settings?.twitter || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            const settingsArray = Object.keys(values).map((fieldName) => ({
              setting_name: 'social',
              setting_slug: fieldName,
              setting_value:
                values[fieldName] === '' ? null : values[fieldName],
              is_active: true,
            }));

            jwtAxios
              .put(`/app-settings/social/`, settingsArray)
              .then((response) => {
                setSubmitting(false);
                setIsEdit(false);
                fetchSettings();
                infoViewActionsContext.showMessage(
                  'Social Settings updated successfully!',
                );
              })
              .catch((error) => {
                infoViewActionsContext.fetchError(error.message);
                setSubmitting(false);
              });
          }}
        >
          {({ resetForm, setFieldValue, touched, errors, values }) => (
            <Form noValidate autoComplete='off'>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant='h4'
                    sx={{
                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'text.primary',
                    }}
                  >
                    Social Settings
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ mt: 2, color: 'text.secondary' }}
                  >
                    These settings allow you to integrate with popular social
                    media sites.
                  </Typography>
                </Box>
                <Box>
                  {!isEdit && (
                    <EditIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsEdit(true)}
                    />
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 4 }} />
              <AppGridContainer>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Box
                    sx={{
                      border: '1px solid #EAECF0',
                      p: 6,
                      borderRadius: 3,
                      boxShadow:
                        '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                    }}
                  >
                    <AppGridContainer>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='facebook'
                          variant='outlined'
                          value={values.facebook}
                          onChange={(event) => {
                            setFieldValue(
                              'facebook',
                              event.target.value === ''
                                ? null
                                : event.target.value,
                            );
                          }}
                          error={touched.facebook && Boolean(errors.facebook)}
                          helperText={touched.facebook && errors.facebook}
                          sx={{
                            width: '100%',
                          }}
                          label='Facebook'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='twitter'
                          variant='outlined'
                          value={values.twitter}
                          onChange={(event) => {
                            setFieldValue(
                              'twitter',
                              event.target.value === ''
                                ? null
                                : event.target.value,
                            );
                          }}
                          error={touched.twitter && Boolean(errors.twitter)}
                          helperText={touched.twitter && errors.twitter}
                          sx={{
                            width: '100%',
                          }}
                          label='X'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='instagram'
                          variant='outlined'
                          value={values.instagram}
                          onChange={(event) => {
                            setFieldValue(
                              'instagram',
                              event.target.value === ''
                                ? null
                                : event.target.value,
                            );
                          }}
                          error={touched.instagram && Boolean(errors.instagram)}
                          helperText={touched.instagram && errors.instagram}
                          sx={{
                            width: '100%',
                          }}
                          label='Instagram'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AppTextField
                          name='slack'
                          variant='outlined'
                          value={values.slack}
                          onChange={(event) => {
                            setFieldValue(
                              'slack',
                              event.target.value === ''
                                ? null
                                : event.target.value,
                            );
                          }}
                          error={touched.slack && Boolean(errors.slack)}
                          helperText={touched.slack && errors.slack}
                          sx={{
                            width: '100%',
                          }}
                          label='Slack'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                    </AppGridContainer>
                  </Box>
                </Grid>
              </AppGridContainer>
              {isEdit && (
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  spacing={5}
                  sx={{ mt: 3 }}
                >
                  <Button
                    sx={{
                      borderColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        borderColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8)
                          : alpha('#2196f3', 0.8),
                      },
                      color: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                    }}
                    onClick={() => {
                      setIsEdit(false);
                      resetForm();
                      setFieldValue(values);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    sx={{
                      backgroundColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        backgroundColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8)
                          : alpha('#2196f3', 0.8),
                      },
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              )}
            </Form>
          )}
        </Formik>
      </AppCard>
      <AppInfoView />
    </>
  );
};

export default Social;
Social.propTypes = {
  settings: PropTypes.object,
  fetchSettings: PropTypes.func,
};
